import { useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import useDataset from "datasets/hooks/useDataset";
import useDatasetTableFQNs from "datasets/hooks/useDatasetTableFQNs";
import useDatasetTables from "datasets/pages/tables/hooks/useDatasetTables";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import rumClient, { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API } = import.meta.env;

const RemoveTableModal = ({
  open,
  onClose,
  table,
  search,
}: {
  open: boolean;
  onClose: () => void;
  table: DatasetTableResponse;
  search: string;
}) => {
  const { datasetId } = useParams();
  const { mutate: mutateDataset } = useDataset();
  const { data: tableFQNs, mutate: mutateFQNs } = useDatasetTableFQNs();
  const { mutate: mutateTables } = useDatasetTables(search);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    if (!tableFQNs) return;

    setError("");
    setRemoving(true);

    try {
      const updatedTables = tableFQNs.filter((fqn) => fqn !== table.fullyQualifiedName);
      const resp = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tables: updatedTables }),
      });

      if (!resp.ok) {
        throw Error("Unable to remove table from dataset");
      }

      const data = await resp.json();

      trackEvent("dataset_list_table_removed", {
        dataset_id: datasetId,
        table_fqn: table.fullyQualifiedName,
      });

      mutateFQNs();
      mutateTables();
      mutateDataset(data);
      onClose();
    } catch (e: any) {
      console.error(e);
      rumClient?.recordError(e);
      setError(e.message);
    }

    setRemoving(false);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>Remove &quot;{table.name}&quot;</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2">
          {"Remove the association between the dataset and the table?"}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {
            "The table will continue to exist. Unless the table is associated with another of your datasets, you will also lose your permission to the table."
          }
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={removing}
          onClick={handleRemove}
          sx={{ bgcolor: "attention.main", "&:hover": { bgcolor: "attention.dark" } }}
        >
          Remove
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveTableModal;
